<template>
  <div>
    <template v-if="domains">
      <table
        class="table table-striped table-hover"
        v-bind:class="{ 'table-overlay': loading }"
      >
        <thead>
          <tr>
            <th>Domain</th>
            <th class="text-left" v-if="!account">Account</th>
            <th class="text-center">Aliases / Rules</th>
            <th class="text-center">SMTP Credentials</th>
            <th class="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="domain in domains" v-bind:key="domain.domain">
            <td>
              <template v-if="domain.active"
                ><span class="pill pill-success"></span
              ></template>
              <template v-else><span class="pill pill-danger"></span></template>
              <router-link
                :to="{
                  name: 'domain-details',
                  params: { domain: domain.domain },
                }"
                >{{ domain.display }}</router-link
              >
              <span
                v-if="domain.banned"
                v-bind:title="
                  domain.ban_reason ? 'banned: ' + domain.ban_reason : 'banned'
                "
                >&#x1f512;</span
              >
              <small
                v-if="domain.banned && domain.ban_reason"
                class="text-muted"
                >banned: {{ domain.ban_reason }}</small
              >
            </td>
            <td class="text-left" v-if="!account">
              <router-link
                :to="{
                  name: 'account-details',
                  params: { email: domain.account.email },
                }"
                >{{ domain.account.name }}</router-link
              >
            </td>
            <td class="text-center">
              <span
                v-if="domain.routing_engine === 'rules'"
                class="badge badge-warning"
                >{{ domain.rules | pluralize("rule", "rules") }}</span
              >
              <span v-else class="badge badge-primary">{{
                domain.aliases | pluralize("alias", "aliases")
              }}</span>
            </td>
            <td class="text-center">
              <router-link
                :to="{
                  name: 'domain-smtp-credentials',
                  params: { domain: domain.domain },
                }"
              >
                <span class="badge badge-secondary">{{
                  domain.smtp_credentials
                    | pluralize("credential", "credentials")
                }}</span>
              </router-link>
            </td>
            <td class="text-center">
              <a
                href="javascript:;"
                title="Remove this domain"
                class="btn btn-danger btn-xs p-2"
                v-on:click="removeDomain(domain)"
                >Remove</a
              >
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td :colspan="account ? 2 : 3">
              <template v-if="this.offset > 0">
                <a
                  href="javascript:;"
                  title="View previous page"
                  v-on:click="previousPage"
                  class="btn btn-primary btn-sm"
                  >Previous</a
                >
              </template>
            </td>
            <td :colspan="account ? 3 : 2" class="text-right">
              <template v-if="(this.offset + 1) * this.limit < this.total">
                <a
                  href="javascript:;"
                  title="View next page"
                  v-on:click="nextPage"
                  class="btn btn-primary btn-sm"
                  >Next</a
                >
              </template>
            </td>
          </tr>
        </tfoot>
      </table>
    </template>
    <template v-else>
      <div
        v-if="loading"
        class="alert alert-info"
        role="alert"
        style="margin-top: 30px"
      >
        <em>Loading the domains, please wait ...</em>
      </div>
      <div
        v-else
        class="alert alert-warning"
        role="alert"
        style="margin-top: 30px"
      >
        <em>No domains found.</em>
      </div>
    </template>
  </div>
</template>

<script>
import { formatHttpError } from "../utils/errors";

export default {
  props: {
    account: String,
    q: String,
    state: { type: String, required: false, default: "all" },
    redirect: { type: Boolean, required: false, default: true },
  },
  data() {
    return {
      loading: false,
      offset: 0,
      limit: 20,
      domains: [],
      total: null,
    };
  },
  watch: {
    q: function (val) {
      this.offset = 0;
      this.search();
    },
    state: function (val) {
      this.offset = 0;
      this.search();
    },
  },
  created() {
    this.search();
  },
  methods: {
    search() {
      this.loading = true;
      let params = {
        account: this.account,
        q: this.q,
        state: this.state,
        offset: this.offset,
        limit: this.limit,
      };

      this.$http.post("domains", params).then(
        (response) => {
          this.domains = response.body.items;
          this.total = response.body.total;
          if (this.total === 1 && this.redirect) {
            this.$router.push({
              name: "domain-details",
              params: { domain: response.body.items[0].domain },
            });
            return;
          }
          this.loading = false;
          this.$emit("updateDomainDetails", {
            total: this.total,
            offset: this.offset,
            limit: this.limit,
            state: this.state,
            q: this.q,
          });
        },
        (response) => {
          if (response.status === 401) return;
          alert(formatHttpError(response));
          this.loading = false;
        },
      );
    },
    nextPage() {
      this.offset += 1;
      if (this.offset * this.limit > this.total) {
        this.offset -= 1;
        return;
      }

      this.search();
    },
    previousPage() {
      if (this.offset === 0) {
        return;
      }
      this.offset -= 1;
      this.search();
    },
    removeDomain(domain) {
      if (confirm("Are you sure you want to remove " + domain.display + " ?")) {
        this.$http.delete("domains/" + domain.domain).then(
          (response) => {
            this.search();
          },
          (response) => {
            if (response.status === 401) return;
            alert(formatHttpError(response));
          },
        );
      }
    },
  },
};
</script>

<style>
table.table-overlay {
  position: relative;
}

table.table-overlay::after {
  content: "";
  display: block;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 100;
}

.btn-xs {
  padding: 0.25rem 0.4rem;
  font-size: 0.875rem;
  line-height: 0.5;
  border-radius: 0.2rem;
}
</style>
